import React, { useState, useEffect, useContext, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AuthContext } from '../AuthContext';
import { useCurrency, CURRENCIES } from '../CurrencyContext';
import { QUERY_KEYS } from '../TransactionService';
import api from '../api';
import './Settings.css';

const CYCLE_OPTIONS = [
  { value: 'month_last_friday', label: 'Last Friday of the month', icon: '📅' },
  { value: 'month_last_day', label: 'Last day of the month', icon: '🗓️' },
  { value: 'manual', label: 'Manual entry (pick a day)', icon: '✏️' },
];

export default function Settings({ onClose }) {
  const { user, updateUser } = useContext(AuthContext);
  const { currency, setCurrency } = useCurrency();
  const queryClient = useQueryClient();
  const fileRef = useRef(null);

  const [cycle, setCycle] = useState('month_last_day');
  const [manualDay, setManualDay] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [currencySearch, setCurrencySearch] = useState('');

  // ── Security / Sessions ──
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [signingOutOthers, setSigningOutOthers] = useState(false);
  const [securityMessage, setSecurityMessage] = useState('');

  const fetchSessions = async () => {
    setSessionsLoading(true);
    try {
      const { data } = await api.get('/api/auth/sessions');
      setSessions(data?.sessions || []);
    } catch (err) {
      console.warn('Could not load sessions:', err);
    } finally {
      setSessionsLoading(false);
    }
  };

  const handleSignOutOthers = async () => {
    if (!window.confirm('Sign out from all other devices? You will stay signed in on this one.')) return;
    setSigningOutOthers(true);
    setSecurityMessage('');
    try {
      await api.post('/api/auth/sign-out-others');
      setSecurityMessage('Signed out from all other devices.');
      fetchSessions();
    } catch (err) {
      setSecurityMessage(err.response?.data?.error || 'Failed to sign out other devices');
    } finally {
      setSigningOutOthers(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const parseDevice = (ua) => {
    if (!ua) return { device: 'Unknown device', browser: '' };
    const uaStr = String(ua);
    let device = 'Desktop';
    if (/iPhone|iPod/i.test(uaStr)) device = 'iPhone';
    else if (/iPad/i.test(uaStr)) device = 'iPad';
    else if (/Android/i.test(uaStr)) device = /Mobile/i.test(uaStr) ? 'Android phone' : 'Android tablet';
    else if (/Macintosh/i.test(uaStr)) device = 'Mac';
    else if (/Windows/i.test(uaStr)) device = 'Windows PC';
    else if (/Linux/i.test(uaStr)) device = 'Linux';

    let browser = '';
    if (/Edg\//i.test(uaStr)) browser = 'Edge';
    else if (/Chrome\//i.test(uaStr)) browser = 'Chrome';
    else if (/Firefox\//i.test(uaStr)) browser = 'Firefox';
    else if (/Safari\//i.test(uaStr)) browser = 'Safari';
    return { device, browser };
  };

  // Load settings on mount
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/api/profile/settings');
        if (data.billing_cycle) setCycle(data.billing_cycle);
        if (data.billing_manual_day) setManualDay(data.billing_manual_day);
      } catch (_) { }
    })();
  }, []);

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => setAvatarPreview(ev.target.result);
    reader.readAsDataURL(file);

    setUploading(true);
    try {
      const form = new FormData();
      form.append('avatar', file);
      const { data } = await api.post('/api/profile/upload-avatar', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (data?.avatar_url) updateUser({ avatar_url: data.avatar_url });
    } catch (err) {
      console.warn('Avatar upload failed:', err);
    }
    setUploading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        billing_cycle: cycle,
        billing_manual_day: parseInt(manualDay) || 1,
      };
      await api.put('/api/profile/settings', payload);
      // Push the new values into the shared cache so the Dashboard's
      // useProfileSettings() (and its cycle-date effect) sees them
      // immediately — without waiting for a network refetch.
      queryClient.setQueryData(QUERY_KEYS.profileSettings(), (prev) => ({
        ...(prev || {}),
        ...payload,
      }));
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.profileSettings() });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (_) { }
    setSaving(false);
  };

  const displayName = user?.name || user?.email || 'User';
  const initials = (displayName[0] || '?').toUpperCase();
  const avatarSrc = avatarPreview || user?.avatar_url;

  const filteredCurrencies = CURRENCIES.filter(c =>
    c.name.toLowerCase().includes(currencySearch.toLowerCase()) ||
    c.code.toLowerCase().includes(currencySearch.toLowerCase()) ||
    c.symbol.includes(currencySearch)
  );

  return (
    <div className="st-overlay" onClick={onClose}>
      <div className="st-panel" onClick={e => e.stopPropagation()}>
        <button className="st-close" onClick={onClose} aria-label="Close settings" title="Close (Esc)">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <line x1="2" y1="2" x2="12" y2="12" />
            <line x1="12" y1="2" x2="2" y2="12" />
          </svg>
        </button>
        <h2 className="st-title">Settings</h2>

        {/* ── Profile Section ── */}
        <div className="st-section">
          <div className="st-section-hd">Profile</div>
          <div className="st-profile-row">
            <div className="st-avatar-wrap" onClick={() => fileRef.current?.click()}>
              {avatarSrc ? (
                <img src={avatarSrc} alt={displayName} className="st-avatar-img"
                  referrerPolicy="no-referrer"
                  onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
              ) : null}
              <div className="st-avatar-fallback" style={avatarSrc ? { display: 'none' } : {}}>{initials}</div>
              <div className="st-avatar-overlay">
                {uploading ? <span className="st-avatar-spinner" /> : '📷'}
              </div>
              <input ref={fileRef} type="file" accept="image/*"
                style={{ display: 'none' }} onChange={handleAvatarChange} />
            </div>
            <div className="st-profile-info">
              <div className="st-profile-name">{displayName}</div>
              <div className="st-profile-email">{user?.email}</div>
              <button className="st-change-photo" onClick={() => fileRef.current?.click()}>
                Change photo
              </button>
            </div>
          </div>
        </div>

        {/* ── Currency Section ── */}
        <div className="st-section">
          <div className="st-section-hd">Currency</div>
          <div className="st-section-desc">
            Choose your default currency — used across all transactions.
          </div>

          {/* Current selection badge */}
          <div className="st-currency-current">
            <span className="st-currency-current-flag">{currency.flag}</span>
            <span className="st-currency-current-sym">{currency.symbol}</span>
            <span className="st-currency-current-name">{currency.name}</span>
            <span className="st-currency-current-code">{currency.code}</span>
          </div>

          {/* Search */}
          <input
            className="st-currency-search"
            type="text"
            placeholder="Search currency…"
            value={currencySearch}
            onChange={e => setCurrencySearch(e.target.value)}
          />

          {/* Currency grid */}
          <div className="st-currency-grid">
            {filteredCurrencies.map(c => (
              <button
                key={c.code}
                className={`st-currency-tile${currency.code === c.code ? ' active' : ''}`}
                onClick={() => setCurrency(c.code)}
                type="button"
                title={c.name}
              >
                <span className="st-currency-tile-flag">{c.flag}</span>
                <span className="st-currency-tile-sym">{c.symbol}</span>
                <span className="st-currency-tile-code">{c.code}</span>
                {currency.code === c.code && <span className="st-currency-tile-check">✓</span>}
              </button>
            ))}
            {filteredCurrencies.length === 0 && (
              <div className="st-currency-empty">No currencies found</div>
            )}
          </div>
        </div>

        {/* ── Billing Cycle Section ── */}
        <div className="st-section">
          <div className="st-section-hd">Billing Cycle</div>
          <div className="st-section-desc">When does your billing period reset?</div>
          <div className="st-cycle-options">
            {CYCLE_OPTIONS.map(opt => (
              <button
                key={opt.value}
                className={`st-cycle-btn${cycle === opt.value ? ' active' : ''}`}
                onClick={() => setCycle(opt.value)}
              >
                <span className="st-cycle-ico">{opt.icon}</span>
                <span className="st-cycle-label">{opt.label}</span>
                {cycle === opt.value && <span className="st-cycle-check">✓</span>}
              </button>
            ))}
          </div>
          {cycle === 'manual' && (
            <div className="st-manual-day">
              <label className="st-lbl">Day of month (1-31)</label>
              <input
                className="st-inp"
                type="number"
                min="1"
                max="31"
                value={manualDay}
                onChange={e => setManualDay(e.target.value)}
              />
            </div>
          )}
        </div>

        {/* ── Security / Devices Section ── */}
        <div className="st-section">
          <div className="st-section-hd">Security & Devices</div>
          <div className="st-section-desc">
            See where you're signed in. You can sign out of other devices while staying logged in here.
          </div>

          <div className="st-devices">
            {sessionsLoading && sessions.length === 0 ? (
              <div className="st-devices-empty">Loading devices…</div>
            ) : sessions.length === 0 ? (
              <div className="st-devices-empty">No active sessions found.</div>
            ) : (
              sessions.map((s, idx) => {
                const { device, browser } = parseDevice(s.user_agent);
                const when = s.updated_at || s.refreshed_at || s.created_at;
                return (
                  <div key={s.id || idx} className={`st-device-row${s.is_current ? ' current' : ''}`}>
                    <div className="st-device-icon">
                      {/Mac|iPhone|iPad|iPod/i.test(s.user_agent || '') ? '🍎'
                        : /Android/i.test(s.user_agent || '') ? '🤖'
                        : /Windows/i.test(s.user_agent || '') ? '🖥️'
                        : '💻'}
                    </div>
                    <div className="st-device-info">
                      <div className="st-device-name">
                        {device}{browser ? ` · ${browser}` : ''}
                        {s.is_current && <span className="st-device-badge">This device</span>}
                      </div>
                      <div className="st-device-meta">
                        {s.ip ? `IP: ${s.ip}` : ''}
                        {when ? ` · Last active: ${new Date(when).toLocaleString()}` : ''}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {securityMessage && (
            <div className="st-security-msg">{securityMessage}</div>
          )}

          <button
            className="st-signout-others"
            onClick={handleSignOutOthers}
            disabled={signingOutOthers}
            type="button"
          >
            {signingOutOthers ? 'Signing out…' : 'Sign out from all other devices'}
          </button>
        </div>

        {/* ── Save ── */}
        <button className={`st-save${saved ? ' saved' : ''}`} onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : saved ? '✓ Saved' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}
